// Library Methods
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";
import { useSnackbarContext } from "src/contexts/alerts/SnackbarContext";

// Types and API
import { updateExamReportComment } from "../../api";

export const useUpdateExamReportComment = (clinicId: string | undefined) => {
  const queryClient = useQueryClient();
  const { getToken } = useTokenAccess();
  const { t } = useTranslation();
  const { setSnackbarInfo } = useSnackbarContext();

  return useMutation({
    mutationFn: async (params: {
      patientId: string;
      examId: string;
      sectionId: string;
      commentId: string;
      commentData: { content: string; conditions?: string | undefined };
    }) => {
      if (!clinicId) throw new Error();

      try {
        const token = await getToken();
        return updateExamReportComment(
          token,
          clinicId,
          params.patientId,
          params.examId,
          params.sectionId,
          params.commentId,
          params.commentData
        );
      } catch (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["report-section-comments"] });
      queryClient.invalidateQueries({ queryKey: ["exam-report-info"] });
      queryClient.invalidateQueries({ queryKey: ["exam-access-info"] });
      queryClient.invalidateQueries({ queryKey: ["exam-report-file"] });
      setSnackbarInfo({
        variant: "success",
        message: t("Comment updated successfully!"),
      });
    },
    onError: () => {
      setSnackbarInfo({
        variant: "error",
        message: t("Comment could not be updated!"),
      });
    },
  });
};
